/**
 * 平台品牌（Logo 切换 / 隐藏，BRAND-LOGO）。
 *
 * `GET /branding` 是**公开**接口：登录页在拿到任何 token 之前就要按它渲染，
 * 所以这里绝不能依赖 auth store。拉不到时回退 `default` —— 品牌显示是增益，
 * 不能因为它把登录页搞白屏。
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { get } from '@/api/client'
import defaultLogoUrl from '@/assets/brand/fusion-ai-logo.png'

export interface BrandingState {
  logo_mode: 'default' | 'custom' | 'hidden'
  logo_version: string | null
  /** 自定义品牌字样；null = 用默认。旧后端没有这个字段时同样按 null 处理。 */
  brand_text?: string | null
}

/** 默认品牌字样 —— 未定制时顶栏、登录页与文档标题都用它。 */
export const DEFAULT_BRAND_TEXT = '福鑫数科 · 训推一体化平台'

export const useBrandingStore = defineStore('branding', () => {
  const state = ref<BrandingState>({ logo_mode: 'default', logo_version: null, brand_text: null })
  const loaded = ref(false)
  let inflight: Promise<void> | null = null

  async function ensureLoaded(): Promise<void> {
    if (loaded.value) return
    inflight ??= (async () => {
      try {
        state.value = await get<BrandingState>('/branding')
      } catch {
        // 公开接口拉不到（后端旧版本没有这个端点、网络问题）：保持默认。
        // 静默是刻意的 —— 登录页上为品牌接口弹错误只会让人以为登录坏了。
      } finally {
        loaded.value = true
        inflight = null
      }
    })()
    await inflight
  }

  /** 上传/切换之后调：下次读取重新拉。 */
  function invalidate(): void {
    loaded.value = false
  }

  const mode = computed(() => state.value.logo_mode)

  /** 原始定制字样；null = 未定制（消费方据此保留各自的默认排版）。 */
  const customText = computed<string | null>(() => state.value.brand_text ?? null)

  /** 展示用品牌字样，未定制时回退默认。 */
  const brandText = computed(() => customText.value ?? DEFAULT_BRAND_TEXT)

  /**
   * `<img>` 的 src。`hidden` 时为 null —— 消费方据此整个不渲染。
   * 自定义图带内容哈希版本号：同图恒同址，`immutable` 长缓存换图立即生效。
   */
  const logoUrl = computed<string | null>(() => {
    if (state.value.logo_mode === 'hidden') return null
    if (state.value.logo_mode === 'custom' && state.value.logo_version) {
      return `/api/v1/branding/logo?v=${state.value.logo_version}`
    }
    return defaultLogoUrl
  })

  return { state, loaded, mode, customText, brandText, logoUrl, ensureLoaded, invalidate }
})
